import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { blogPostsData, BlogPost } from '@/app/SharedBlogData';
import { format } from 'date-fns';

// Category display name map for consistency (Copied from BlogContent.tsx)
const categoryDisplayName: { [key: string]: string } = {
  ai: 'AI & Technology',
  analytics: 'Analytics & Insights',
  commerce: 'E-commerce & Marketplaces',
  'customer-experience': 'Customer Experience & Loyalty',
  entrepreneurship: 'Entrepreneurship',
  growth: 'Growth & Case Studies',
  policy: 'Global Policy'
};

// Sort posts by date and return the latest N posts
const getRecentPosts = (limit: number): BlogPost[] => {
  // Use a defensive copy and sort by datePublished descending
  const sortedPosts = [...blogPostsData].sort((a, b) => 
    new Date(b.datePublished).getTime() - new Date(a.datePublished).getTime()
  );
  return sortedPosts.slice(0, limit);
};

// Component to display the latest blog posts on the homepage
export function LatestBlogPosts() {
  // Fetch the 3 most recent posts
  const posts = getRecentPosts(3);
  
  if (posts.length === 0) {
    return null; // Don't render if no posts are available
  }

  return (
    <section className="section bg-gray-50">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="mb-4">
            Latest Insights from the Agentic Web
          </h2>
          <p className="max-w-3xl mx-auto text-lg">
            Stay up-to-date with our latest thoughts on AI-powered commerce, marketplaces, and the future of the web.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <div key={post.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <Link href={post.customPath} className="block">
                <Image
                  src={post.image}
                  alt={post.title}
                  width={400} // Smaller size for the grid view
                  height={192}
                  className="w-full h-48 object-cover rounded-t-lg hover:opacity-90 transition-opacity"
                  style={{ aspectRatio: '25/12' }}
                  sizes="(max-width: 1024px) 100vw, 33vw"
                />
              </Link>
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <span className="bg-[#E6F7F9] text-[#00A4BD] text-xs px-2 py-1 rounded-full">
                    {categoryDisplayName[post.category] || post.category}
                  </span>
                  <span className="text-gray-500 text-xs ml-3">{format(new Date(post.date), 'MMMM d, yyyy')}</span>
                </div>
                <h3 className="text-xl mb-3">
                  <Link href={post.customPath} className="hover:text-[#008CA1]">
                    {post.title}
                  </Link>
                </h3>
                <p className="mb-4 text-sm">{post.excerpt}</p>
                <div className="flex items-center mb-4">
                  <Link href={`/team/${post.author.id}`} className="flex items-center hover:opacity-80 transition-opacity">
                    <Image
                      src={post.author.image}
                      alt={post.author.name}
                      width={32}
                      height={32}
                      className="w-8 h-8 object-cover rounded-full mr-2"
                    />
                    <div>
                      <p className="text-sm font-medium hover:text-[#00A4BD] transition-colors">{post.author.name}</p>
                      <p className="text-xs text-gray-600">{post.author.jobTitle}</p>
                    </div>
                  </Link>
                </div>
                <Link href={post.customPath} className="text-[#00A4BD] font-medium hover:text-[#008CA1] text-sm">
                  Read Article →
                </Link>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <Link href="/blog" className="btn-secondary inline-block">
            View All Blog Posts
          </Link>
        </div>
      </div>
    </section>
  );
}